import type { AuthSession } from 'aws-amplify/auth';

export const REDIRECT_PARAM_NAME = 'redirectTo';

/** SessionStorage key for SSO redirect; survives OAuth round-trip since callback URL has no query. */
export const SSO_REDIRECT_STORAGE_KEY = 'ssoRedirectTo';

/** SessionStorage key: email entered on the SSO form before signInWithRedirect (callback compares to id token). */
export const SSO_ATTEMPTED_EMAIL_STORAGE_KEY = 'ssoAttemptedLoginEmail';

/** SessionStorage key: email entered on the SSO form before signInWithRedirect (callback compares to id token). */
export const CLARIUM_DOMAIN_SESSION_AUTH_STORAGE_KEY = 'clariumDomainSessionAuth';

export function normalizeEmailForSsoComparison(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * After OAuth callback, if we stored an attempted SSO email, compare it to the Cognito id token email.
 * Clears the attempted-email storage key when checked. Returns mismatch details for UI when blocked.
 */
export function isSsoSessionEmailMismatch(session: AuthSession | null | undefined): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  let attempted: string | null;
  try {
    attempted = sessionStorage.getItem(SSO_ATTEMPTED_EMAIL_STORAGE_KEY);
  } catch {
    console.log('no session storage');
    return false;
  }

  if (!attempted) {
    return false;
  }

  const clearAttempted = () => {
    try {
      sessionStorage.removeItem(SSO_ATTEMPTED_EMAIL_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const loggedInEmail = session?.tokens?.idToken?.payload?.email;

  if (typeof loggedInEmail !== 'string') {
    clearAttempted();
    return true;
  }

  const normalizedAttempted = normalizeEmailForSsoComparison(attempted);
  const normalizedSession = normalizeEmailForSsoComparison(loggedInEmail);
  if (normalizedSession !== normalizedAttempted) {
    clearAttempted();
    return true;
  }

  clearAttempted();
  return false;
}

/** Ensures path has a leading slash so it is treated as absolute (e.g. for navigate/window.location). */
export function ensureLeadingSlash(path: string): string {
  const trimmed = path.trim();
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/**
 * Returns true if the path is safe for redirect (same-origin absolute path).
 * Rejects protocol-relative URLs (e.g. //attacker.com) and paths containing "login" to prevent open redirects.
 */
export function isSafeRedirectPath(path: string): boolean {
  const t = path.trim();
  if (t === '' || t === '/') return true;
  if (t.startsWith('//')) return false;
  // Block redirects to login page to prevent loops (case-insensitive, matches /login or /login?...)
  const pathOnly = t.split('?')[0].toLowerCase();
  if (pathOnly === '/login' || pathOnly.startsWith('/login/')) return false;
  return t.startsWith('/');
}

export function loginRedirectURL(url: URL) {
  const redirectTo = new URLSearchParams([[REDIRECT_PARAM_NAME, `${url.pathname}${url.search}`]]);
  return `/login?${redirectTo.toString()}`;
}

export enum AuthTypeEnum {
  USER_PASSWORD_AUTH = 'USER_PASSWORD_AUTH',
}

export { useAuthType } from './auth-hooks';
